import { Link, NavLink, Outlet, useMatch } from "react-router-dom";
import ThemeToggle from "@/theme/ThemeToggle";
import AppLauncher from "@/components/AppLauncher";
import UserMenu from "@/components/UserMenu";
import Logo from "@/components/Logo";
import Icon, { type IconName } from "@/components/Icon";

/** Coque de l'espace enseignant : sidebar de navigation fixe + barre haute (thème /
 *  apps / compte), le contenu de chaque section étant rendu via <Outlet/>. Remplace
 *  l'ancienne modale « Mes cours » par de vraies pages (URL, favori, précédent/suivant). */

const NAV: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: "/instructor/dashboard", label: "Tableau de bord", icon: "home" },
  { to: "/instructor/courses",   label: "Cours",           icon: "checklist", end: true },
  { to: "/instructor/archives",  label: "Archives",        icon: "file" },
  { to: "/instructor/settings",  label: "Réglages",        icon: "more" },
];

export default function InstructorShell() {
  // Mode « focus » : l'éditeur de contenu (/instructor/c/:courseId) occupe toute la
  // largeur (la nav latérale est masquée), mais garde la barre haute commune. Match
  // EXACT pour ne pas déclencher le focus sur d'autres routes /instructor/*.
  const focus = !!useMatch({ path: "/instructor/c/:courseId", end: true });

  return (
    <div style={styles.page}>
      {!focus && (
        <aside style={styles.sidebar}>
          <Link to="/" style={styles.brand} title="Accueil Python Learning">
            <Logo size={26} />
            <span style={styles.brandText}>Enseignant</span>
          </Link>
          <nav style={styles.nav}>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                style={({ isActive }) => ({ ...styles.navItem, ...(isActive ? styles.navItemActive : {}) })}
              >
                <Icon name={item.icon} size={17} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
      )}

      <div style={styles.content}>
        <header style={styles.topbar}>
          {/* En mode focus la sidebar (et son logo) est masquée → on remet un logo
              cliquable à gauche pour ne pas enfermer l'utilisateur dans l'éditeur. */}
          {focus && (
            <Link to="/" style={styles.topbarLogo} title="Accueil Python Learning">
              <Logo size={24} />
            </Link>
          )}
          <span style={{ flex: 1 }} />
          <ThemeToggle />
          <AppLauncher />
          <UserMenu />
        </header>
        <main style={focus ? styles.mainFocus : styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { display: "flex", height: "100vh", background: "var(--bg-base)", color: "var(--text)", overflow: "hidden" },
  sidebar: {
    width: 220, flexShrink: 0, background: "var(--bg-bar)", borderRight: "1px solid var(--border-soft)",
    display: "flex", flexDirection: "column", padding: "16px 12px", gap: 18,
  },
  brand: { display: "flex", alignItems: "center", gap: 10, padding: "4px 8px", textDecoration: "none", color: "inherit", borderRadius: "var(--radius)" },
  brandText: { fontWeight: 700, fontSize: 16, color: "var(--text)" },
  topbarLogo: { display: "inline-flex", alignItems: "center", padding: 4, borderRadius: "var(--radius-sm)" },
  nav: { display: "flex", flexDirection: "column", gap: 2 },
  navItem: {
    display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: "var(--radius)",
    color: "var(--text-muted)", textDecoration: "none", fontSize: 14, fontWeight: 600,
  },
  navItemActive: { background: "var(--badge-bg)", color: "var(--blue)" },

  content: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  topbar: {
    display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12,
    padding: "12px 24px", borderBottom: "1px solid var(--border-soft)", background: "var(--bg-bar)", flexShrink: 0,
  },
  main: { flex: 1, overflowY: "auto", padding: "28px 32px" },
  // Mode focus : l'éditeur gère son propre scroll/padding et remplit la zone.
  mainFocus: { flex: 1, minHeight: 0, display: "flex", overflow: "hidden" },
};
